import { assoc } from 'ramda';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';

import { SUPPLIER_TABLE_COLUMN_TYPES } from 'constants/all';

import purchasedComponentsService from 'services/purchasedComponentsService';

import { getGroupId } from 'store/session/selectors';

import { createNumericComparator } from 'utils/helpers';

import {
  actionModal,
  customColumnsCreate,
  customColumnsDelete,
  customColumnsReceive,
  customColumnsReorder,
  customColumnsUpdate,
  listPage,
  purchasedComponentsList,
  types,
  uploadPurchasedComponents,
} from './actions';
import {
  getActivePurchasedComponents,
  getPage,
  getPageSize,
  getSearchString,
  getSortedCustomColumns,
  getSortString,
} from './reducer';

export default function* root() {
  yield takeLatest(types.PURCHASED_COMPONENTS_LIST_REQUEST, watchListRequest);
  yield takeLatest(types.LIST_PAGE_PAGINATE, watchSearch);
  yield takeLatest(types.LIST_PAGE_SEARCH, watchSearch);
  yield takeLatest(types.LIST_PAGE_SORT, watchSearch);
  yield takeLatest(
    types.UPLOAD_PURCHASED_COMPONENTS_REQUEST,
    watchUploadPurchasedComponentsRequest,
  );
  yield takeLatest(
    types.PURCHASED_COMPONENT_CREATE_SUCCESS,
    watchCreatePurchasedComponent,
  );
  yield takeLatest(
    types.PURCHASED_COMPONENT_UPDATE_SUCCESS,
    watchUpdatePurchasedComponent,
  );
  yield takeLatest(
    types.PURCHASED_COMPONENT_DELETE_SUCCESS,
    watchDeleteSuccess,
  );
  yield takeLatest(types.LIST_PAGE_MARK_TABLE_DIRTY, updateTableData);
  yield takeLatest(
    types.CUSTOM_COLUMNS_REORDER_FLOW,
    watchReorderCustomColumnsFlow,
  );
  yield takeLatest(
    types.CUSTOM_COLUMNS_CREATE_REQUEST,
    watchCreateCustomColumn,
  );
  yield takeLatest(
    types.CUSTOM_COLUMNS_UPDATE_REQUEST,
    watchUpdateCustomColumn,
  );
  yield takeLatest(
    types.CUSTOM_COLUMNS_DELETE_REQUEST,
    watchDeleteCustomColumn,
  );
}

export function* initPurchasedComponentsPage() {
  const groupId = yield select(getGroupId);
  try {
    const response = yield call(
      purchasedComponentsService.customColumns.list,
      groupId,
    );
    yield put(customColumnsReceive(response));
  } catch (error) {
    console.error('Could not fetch custom columns', error);
  }
  yield put(purchasedComponentsList.request());
}

function* watchUploadPurchasedComponentsRequest(action) {
  const { payload } = action;
  const groupId = yield select(getGroupId);
  try {
    yield call(purchasedComponentsService.uploadCsv, groupId, payload);
    yield put(uploadPurchasedComponents.success());
    yield put(actionModal.close());
    yield put(purchasedComponentsList.request());
  } catch (error) {
    yield put(uploadPurchasedComponents.error(error));
  }
}

function* watchListRequest() {
  try {
    const page = yield select(getPage);
    const pageSize = yield select(getPageSize);
    const search = yield select(getSearchString);
    const sortString = yield select(getSortString);
    const queryParams = {
      page,
      pageSize,
      search,
      sort: sortString || undefined,
    };
    const groupId = yield select(getGroupId);

    const response = yield call(
      purchasedComponentsService.list,
      groupId,
      queryParams,
    );
    yield put(purchasedComponentsList.success(response));
    yield put(listPage.markTableDirty());
  } catch (error) {
    yield put(purchasedComponentsList.error(error));
  }
}

function* watchSearch() {
  yield put(purchasedComponentsList.request());
}

// NOTE: comes from reception of save from PurchasedComponentUpdateCreateModal
function* watchCreatePurchasedComponent() {
  yield put(purchasedComponentsList.request());
}

// NOTE: comes from reception of save from PurchasedComponentUpdateCreateModal
function* watchUpdatePurchasedComponent() {
  yield put(listPage.markTableDirty());
}

// NOTE: comes from reception of delete from PurchasedComponentUpdateCreateModal
function* watchDeleteSuccess() {
  yield put(listPage.markTableDirty());
}

function* updateTableData() {
  try {
    const activePurchasedComponents = yield select(
      getActivePurchasedComponents,
    );
    const customColumns = yield select(getSortedCustomColumns);

    const columns = [
      {
        align: 'left',
        dataIndex: 'oem_part_number',
        key: 'oem_part_number',
        title: 'OEM Part Number',
      },
      {
        align: 'left',
        dataIndex: 'internal_part_number',
        key: 'internal_part_number',
        title: 'Internal Part No',
      },
      {
        align: 'right',
        dataIndex: 'piece_price',
        key: 'piece_price',
        title: 'Piece Price',
        isMoney: true,
      },
      {
        align: 'left',
        dataIndex: 'description',
        key: 'description',
        title: 'Description',
      },
    ];
    columns.push(
      ...customColumns.map((col) => {
        return {
          align:
            col.valueType === SUPPLIER_TABLE_COLUMN_TYPES.STRING
              ? 'left'
              : 'right',
          dataIndex: col.codeName,
          key: col.codeName,
          title: col.name,
        };
      }),
    );
    const rows = activePurchasedComponents.map((pc) => {
      const obj = {
        id: pc.id,
        oem_part_number: pc.oemPartNumber,
        internal_part_number: pc.internalPartNumber,
        piece_price: pc.piecePrice,
        description: pc.description,
      };
      for (const d of pc.properties) {
        obj[d.key] = d.value;
      }
      return obj;
    });

    const tableData = {
      columns,
      rows,
    };
    yield put(listPage.receiveTableData({ tableData }));
  } catch (error) {
    console.error('Failed to build table', error);
    const tableData = {
      columns: [],
      rows: [],
    };
    yield put(listPage.receiveTableData({ tableData }));
  }
}

const reorderCustomColumns = (customColumns, oldIndex, newIndex) => {
  const updatePosition = (list) =>
    list.slice().map((item, index) => assoc('position', index + 1, item));

  let result = customColumns.slice().sort(createNumericComparator('position'));
  result = updatePosition(result);

  const target = result[oldIndex];

  result.splice(oldIndex, 1);
  result.splice(newIndex, 0, target);

  return updatePosition(result);
};

function* watchReorderCustomColumnsFlow({ payload }) {
  const { index } = payload;
  const customColumns = yield select(getSortedCustomColumns);
  const column = customColumns[index];

  const reorderAction = yield take(types.CUSTOM_COLUMNS_REORDER_REQUEST);
  const { newIndex } = reorderAction.payload;

  const reorderedCustomColumns = reorderCustomColumns(
    customColumns,
    index,
    newIndex,
  );
  yield put(
    customColumnsReorder.success({ customColumns: reorderedCustomColumns }),
  );

  try {
    yield call(purchasedComponentsService.customColumns.update, column.id, {
      position: newIndex + 1,
    });
  } catch (error) {
    console.error(error);
  }

  yield put(listPage.markTableDirty());
}

function* watchCreateCustomColumn({ payload }) {
  const { data } = payload;
  const groupId = yield select(getGroupId);

  try {
    const response = yield call(
      purchasedComponentsService.customColumns.create,
      groupId,
      data,
    );
    yield put(customColumnsCreate.success({ customColumn: response }));
    yield put(purchasedComponentsList.request());
    yield put(actionModal.close());
  } catch (error) {
    yield put(customColumnsCreate.error(error));
  }
}

function* watchUpdateCustomColumn({ payload }) {
  const { customColumnId, data } = payload;

  try {
    const response = yield call(
      purchasedComponentsService.customColumns.update,
      customColumnId,
      data,
    );
    yield put(customColumnsUpdate.success({ customColumn: response }));
    yield put(purchasedComponentsList.request());
    yield put(actionModal.close());
  } catch (error) {
    yield put(customColumnsUpdate.error(error));
  }
}

function* watchDeleteCustomColumn({ payload }) {
  const { customColumnId } = payload;

  try {
    yield call(purchasedComponentsService.customColumns.delete, customColumnId);
    yield put(customColumnsDelete.success({ customColumnId }));
    yield put(purchasedComponentsList.request());
    yield put(actionModal.close());
  } catch (error) {
    yield put(customColumnsDelete.error(error));
  }
}
